/**
 * views/InventoryReport.tsx — /dashboard/report/:missionId
 * ──────────────────────────────────────────────────────────
 * Customer-facing inventory reconciliation report for a single mission.
 *
 * One row per reconciled location, rendered as the customer A–K report:
 *   Location · License Plate · Description · License Plate WMS · Item WMS ·
 *   Expected Count · Drone Count · Result · Timestamp · Comments · Image.
 *
 * Result is the persisted 3-state projection (Match | Mismatch | N/A) computed
 * server-side in report_projection.py — never recomputed here.
 *
 * Data source : GET /api/inventory/missions/{id}/records  (fetchMissionRecords)
 * Photos      : GET /api/inventory/photos/{photo_id}/image (JWT-gated, via
 *               useAuthImage — a plain <img src> would 401).
 * Excel export: GET /api/inventory/missions/{id}/export.xlsx  (missionExportUrl).
 *               When a Result filter is active, ?result= is appended so the
 *               workbook matches the on-screen view.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Download, Lock, LockOpen, RefreshCw, Search } from 'lucide-react';

import { apiClient, ApiError } from '@/api/client';
import {
  fetchMissionRecords, missionExportUrl, buildPhotoThumbUrl, updateRecordResult,
} from '@/api/inventory';
import { useAuthImage } from '@/hooks/useAuthImage';
import { ImageLightbox } from '@/components/ui/ImageLightbox';
import { useAuth } from '@/contexts/AuthContext';
import {
  reportProfileFor,
  usesOatsideLayout,
  usesNbcLayout,
  usesCevaPlaceholderLayout,
  nbcIdentityHeaders,
  identityHeaders,
  countHeader,
  wmsPlateDisplay,
  type ReportProfile,
} from '@/lib/reportProfile';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import type { CustomerResult, MissionReconciliationData, ReconciliationRecord } from '@/types';

/** Result options for the inline editor, in display order. */
const RESULT_OPTIONS: CustomerResult[] = ['Match', 'Mismatch', 'N/A'];

/** Roles allowed to override a location's Result + Comment. Customers are read-only. */
function canEditResults(role: string | undefined): boolean {
  return role === 'manager' || role === 'admin' || role === 'super_admin';
}

type FilterMode = 'all' | CustomerResult | 'Empty' | 'MissingQR';
type SortDir = 'asc' | 'desc';

/**
 * Result filters, in display order. Match/Mismatch/N/A key off the persisted
 * `customer_result`; "Empty" is cross-cutting — physically empty locations
 * (drone_count === 0) regardless of result, so customers can find empty slots.
 */
const RESULT_FILTERS: FilterMode[] = ['Match', 'Mismatch', 'N/A', 'Empty', 'MissingQR'];

/** Em dash for null/absent numeric cells. */
function num(n: number | null | undefined): string {
  return n == null ? '—' : n.toLocaleString();
}

/** Em dash for null/empty string cells (never fabricates data). */
function txt(s: string | null | undefined): string {
  return s == null || s === '' ? '—' : s;
}

/** Locale timestamp, em dash when absent/invalid. */
function fmtTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** Tailwind colour for the 3-state result chip. */
function resultClass(result: CustomerResult): string {
  if (result === 'Match') return 'text-green-700 bg-green-50';
  if (result === 'Mismatch') return 'text-red-700 bg-red-50';
  return 'text-amber-700 bg-amber-50';
}

/**
 * JWT-gated photo thumbnail. Blank cell when no photo_id.
 *
 * The image is fetched only once the cell scrolls into view (IntersectionObserver),
 * so a long report no longer kicks off thousands of concurrent full-size photo
 * downloads on load — only the handful of visible rows fetch.
 */
function PhotoThumb({ photoId }: { photoId: string | null }) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  const src = useAuthImage(inView && photoId ? buildPhotoThumbUrl(photoId) : null);

  if (!photoId) return <span className="text-muted-foreground">—</span>;
  return (
    <div ref={ref} className="h-10 w-10">
      {src === false ? (
        <span className="text-muted-foreground">—</span>
      ) : src === null ? (
        <div className="h-10 w-10 rounded bg-gray-100 animate-pulse" />
      ) : (
        <img
          src={src}
          alt="Location"
          loading="lazy"
          className="h-10 w-10 rounded object-cover border cursor-zoom-in"
          title="Click to enlarge"
          onClick={() => setLightbox(true)}
        />
      )}
      {lightbox && (
        <ImageLightbox photoId={photoId} onClose={() => setLightbox(false)} />
      )}
    </div>
  );
}

/** Parse a numeric input back to number | null (blank → null). */
function parseCount(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * One report table row.
 *
 * Read-only callers (customers) always see the persisted values. Editors
 * (manager/admin/super_admin) get an "Edit" button in the Result cell; clicking
 * it turns Expected Count, Drone Count, Result, and Comments into inputs seeded
 * from the current values, with Save / Cancel. Save PATCHes the override, then
 * the parent refresh updates the chip colour, comment, counts, and header
 * totals.
 */
function ReportTableRow({
  record,
  profile,
  editable,
  onSaved,
}: {
  record: ReconciliationRecord;
  profile: ReportProfile;
  editable: boolean;
  onSaved: () => Promise<void> | void;
}) {
  const [editing, setEditing]   = useState(false);
  const [result, setResult]     = useState<CustomerResult>(record.customer_result);
  const [comments, setComments] = useState(record.comments === '-' ? '' : record.comments);
  const [expected, setExpected] = useState(record.expected_count == null ? '' : String(record.expected_count));
  const [drone, setDrone]       = useState(record.drone_count == null ? '' : String(record.drone_count));
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);

  // Re-seed local edit state when the underlying record changes (after refresh).
  useEffect(() => {
    setResult(record.customer_result);
    setComments(record.comments === '-' ? '' : record.comments);
    setExpected(record.expected_count == null ? '' : String(record.expected_count));
    setDrone(record.drone_count == null ? '' : String(record.drone_count));
    setEditing(false);
    setError(null);
  }, [record.id, record.customer_result, record.comments, record.expected_count, record.drone_count]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await updateRecordResult(record.id, {
        result,
        comments,
        expected_count: parseCount(expected),
        drone_count: parseCount(drone),
      });
      await onSaved();
      setEditing(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [record.id, result, comments, expected, drone, onSaved]);

  const numInputClass = `h-7 w-20 rounded border border-input bg-white px-2 text-xs text-right
                         focus:outline-none focus:ring-2 focus:ring-ring`;

  if (usesCevaPlaceholderLayout(profile)) {
    return (
      <TableRow style={{ height: ROW_HEIGHT }}>
        <TableCell className="font-medium whitespace-nowrap">{record.location_code}</TableCell>
        <TableCell className="text-muted-foreground">
          CEVA report layout placeholder - final UI format pending.
        </TableCell>
        <TableCell className="whitespace-nowrap">
          <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${resultClass(record.customer_result)}`}>
            {record.customer_result}
          </span>
        </TableCell>
        <TableCell className="whitespace-nowrap">{fmtTs(record.created_at)}</TableCell>
        <TableCell><PhotoThumb photoId={record.photo_id} /></TableCell>
      </TableRow>
    );
  }

  if (usesNbcLayout(profile)) {
    const aisle = record.location_code.split('-', 1)[0] || '';
    const ohDetected = record.detected_sku ? 1 : 0;
    return (
      <TableRow style={{ height: ROW_HEIGHT }}>
        <TableCell className="font-medium whitespace-nowrap">{record.location_code}</TableCell>
        <TableCell className="whitespace-nowrap">{aisle}</TableCell>
        <TableCell className="whitespace-nowrap">{txt(record.detected_sku)}</TableCell>
        <TableCell className="text-right tabular-nums">{ohDetected}</TableCell>
        <TableCell className="whitespace-nowrap">{fmtTs(record.created_at)}</TableCell>
        <TableCell className="whitespace-nowrap">{txt(record.expected_sku)}</TableCell>
        <TableCell className="text-right tabular-nums">{num(record.expected_count)}</TableCell>
        <TableCell className="whitespace-nowrap">
          <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${resultClass(record.customer_result)}`}>
            {record.customer_result}
          </span>
        </TableCell>
        <TableCell className="whitespace-nowrap">{txt(record.item_code)}</TableCell>
        <TableCell>{txt(record.item_description ?? record.product_description)}</TableCell>
        <TableCell className="max-w-xs">{txt(record.comments)}</TableCell>
        <TableCell><PhotoThumb photoId={record.photo_id} /></TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow style={{ height: ROW_HEIGHT }}>
      <TableCell className="font-medium whitespace-nowrap">{record.location_code}</TableCell>
      {usesOatsideLayout(profile) ? (
        <>
          {/* Oatside-family layout (Oatside + Fonterra): detected code ·
              Description · WMS code · Description WMS · Item WMS (mirrors the
              Excel export columns; only the header labels differ per profile). */}
          <TableCell className="whitespace-nowrap">{txt(record.license_plate)}</TableCell>
          <TableCell>{txt(record.detected_description)}</TableCell>
          <TableCell className="whitespace-nowrap">{wmsPlateDisplay(record.expected_sku)}</TableCell>
          <TableCell>{txt(record.product_description)}</TableCell>
          <TableCell className="whitespace-nowrap">{txt(record.item_code)}</TableCell>
        </>
      ) : (
        <>
          <TableCell className="whitespace-nowrap">{txt(record.license_plate)}</TableCell>
          <TableCell>{txt(record.detected_description)}</TableCell>
          <TableCell>{txt(record.product_description)}</TableCell>
          <TableCell className="whitespace-nowrap">{txt(record.sku_batch_wms)}</TableCell>
          <TableCell>{txt(record.item_prefix)}</TableCell>
          <TableCell>{txt(record.product_status)}</TableCell>
        </>
      )}
      <TableCell className="text-right tabular-nums">
        {editing ? (
          <input
            type="number"
            value={expected}
            onChange={e => setExpected(e.target.value)}
            disabled={saving}
            aria-label="Expected Count"
            className={numInputClass}
          />
        ) : (
          num(record.expected_count)
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {editing ? (
          <input
            type="number"
            value={drone}
            onChange={e => setDrone(e.target.value)}
            disabled={saving}
            aria-label="Drone Count"
            className={numInputClass}
          />
        ) : (
          num(record.drone_count)
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <select
              value={result}
              onChange={e => setResult(e.target.value as CustomerResult)}
              disabled={saving}
              aria-label="Result"
              className={`h-7 rounded border border-input px-1.5 text-xs font-medium
                          focus:outline-none focus:ring-2 focus:ring-ring ${resultClass(result)}`}
            >
              {RESULT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <Button size="sm" className="h-7 px-2" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => { setEditing(false); setError(null); }}
              disabled={saving}
            >
              Cancel
            </Button>
            {error && <span className="text-[10px] text-red-600">{error}</span>}
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${resultClass(record.customer_result)}`}>
              {record.customer_result}
            </span>
            {editable && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                onClick={() => { setEditing(true); setError(null); }}
              >
                ✎ Edit
              </Button>
            )}
            {editable && record.override_flag && (
              <span className="text-[10px] text-muted-foreground" title="Manually edited">edited</span>
            )}
          </div>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap">{fmtTs(record.created_at)}</TableCell>
      <TableCell className="max-w-xs">
        {editing ? (
          <input
            type="text"
            value={comments}
            onChange={e => setComments(e.target.value)}
            disabled={saving}
            aria-label="Comments"
            placeholder="Add comment…"
            className="h-7 w-44 rounded border border-input bg-white px-2 text-xs
                       focus:outline-none focus:ring-2 focus:ring-ring"
          />
        ) : (
          txt(record.comments)
        )}
      </TableCell>
      <TableCell><PhotoThumb photoId={record.photo_id} /></TableCell>
    </TableRow>
  );
}

/** Default mission when the route omits one — the Reckitt DHL warehouse. */
const RECKITT_DHL_MISSION_ID = 'e03391af-f673-4ee3-8f88-79a50f2b13bf';

/** Fixed row height (px) used by the windowing math — must match the rendered row. */
const ROW_HEIGHT = 56;
/** Visible scroll viewport height (px). */
const VIEWPORT_HEIGHT = 640;
/** Rows rendered above/below the viewport so fast scrolls don't flash blanks. */
const OVERSCAN = 8;

/**
 * Minimal row virtualization (no external dep). Renders only the rows in (and
 * near) the viewport instead of mounting all ~2756 at once; top/bottom spacer
 * rows preserve scrollbar geometry. Assumes a uniform ROW_HEIGHT.
 */
function useRowWindow(total: number) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) setScrollTop(el.scrollTop);
  }, []);

  // Reset scroll when the list shrinks/changes (filter, search, sort).
  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollTop !== 0) el.scrollTop = 0;
    setScrollTop(0);
  }, [total]);

  const first = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const visibleCount = Math.ceil(VIEWPORT_HEIGHT / ROW_HEIGHT) + OVERSCAN * 2;
  const last = Math.min(total, first + visibleCount);
  const padTop = first * ROW_HEIGHT;
  const padBottom = Math.max(0, (total - last) * ROW_HEIGHT);

  return { scrollRef, onScroll, first, last, padTop, padBottom };
}

export default function InventoryReport() {
  const { missionId: routeMissionId } = useParams<{ missionId: string }>();
  const missionId = routeMissionId ?? RECKITT_DHL_MISSION_ID;
  const navigate = useNavigate();
  const routerState = useLocation().state as { title?: string } | null;
  const missionTitle = routerState?.title ?? null;
  const { user } = useAuth();

  const [data, setData]       = useState<MissionReconciliationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [filter, setFilter]   = useState<FilterMode>('all');
  const [search, setSearch]   = useState('');
  const [searchComment, setSearchComment] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [exporting, setExporting] = useState(false);
  const [freezing, setFreezing]   = useState(false);

  // Report freeze: frozen reports are immutable (server enforces with 409s) —
  // hide the edit affordances entirely so the UI matches.
  const frozenAt  = data?.report_frozen_at ?? null;
  const isManager = canEditResults(user?.role);
  const editable  = isManager && !frozenAt;

  const load = useCallback(async () => {
    if (!missionId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await fetchMissionRecords(missionId));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load reconciliation records.');
    } finally {
      setLoading(false);
    }
  }, [missionId]);

  useEffect(() => { load(); }, [load]);

  // Filter (result) → search (location_code) → sort (location_code).
  const records: ReconciliationRecord[] = useMemo(() => {
    const all = data?.flat_records ?? [];
    const q = search.trim().toLowerCase();
    const qc = searchComment.trim().toLowerCase();
    const filtered = all.filter(r => {
      if (filter === 'Empty') {
        if (r.drone_count !== 0) return false;
      } else if (filter === 'MissingQR') {
        // Cross-cutting: locations whose pallet QR could not be decoded.
        if (!(r.reasoning ?? '').includes('QR cannot be scanned')) return false;
      } else if (filter !== 'all' && r.customer_result !== filter) {
        return false;
      }
      if (q && !r.location_code.toLowerCase().includes(q)) return false;
      if (qc && !(r.comments ?? '').toLowerCase().includes(qc)) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      const cmp = a.location_code.localeCompare(b.location_code);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, filter, search, searchComment, sortDir]);

  // Window the rows so only the visible slice mounts (and only those rows fetch photos).
  const { scrollRef, onScroll, first, last, padTop, padBottom } = useRowWindow(records.length);
  const visibleRecords = records.slice(first, last);

  const summary = data?.summary;
  const discrepancyTotal = summary ? summary.total - summary.match : 0;

  // Per-customer column set — same warehouse-name matching as the Excel export.
  const profile = reportProfileFor(data?.warehouse_name);
  const colCount = usesCevaPlaceholderLayout(profile)
    ? 5
    : (usesNbcLayout(profile) || usesOatsideLayout(profile) ? 12 : 13);

  // ── Authenticated Excel download — mirrors the active Result filter ─────────
  const handleExport = useCallback(async () => {
    if (!missionId) return;
    setExporting(true);
    try {
      const base = missionExportUrl(missionId);
      // Only the server-recognised result projections narrow the workbook.
      // 'all' and the client-only 'Empty' cross-cut filter export everything.
      const url = filter === 'all' || filter === 'Empty'
        ? base
        : `${base}?result=${encodeURIComponent(filter)}`;
      const res = await apiClient.getRaw(url);
      if (!res.ok) { alert(`Export failed (${res.status}).`); return; }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${(missionTitle ?? 'inventory').replace(/[^a-zA-Z0-9_-]/g, '_')}_report.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      alert('Could not download report — check your connection.');
    } finally {
      setExporting(false);
    }
  }, [filter, missionId, missionTitle]);

  const toggleSort = useCallback(() => {
    setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
  }, []);

  // ── Freeze for Next Report ────────────────────────────────────────────────
  // Locks the delivered report before the next flight/SOH cycle: results,
  // photos and comments become immutable, and report display fields stay
  // pinned to the CURRENT SOH snapshot — a new SOH upload covering the same
  // locations cannot change this report. Unfreeze reverses it.
  const handleFreeze = useCallback(async () => {
    if (!missionId || !data) return;
    if (!window.confirm(
      'Freeze this report for the next report cycle?\n\n'
      + '• All results, photos and comments become READ-ONLY.\n'
      + '• The report stays pinned to the CURRENT SOH — uploading a new SOH '
      + 'for the same locations will NOT change it.\n'
      + '• Reconcile / reprocess / edits are blocked until unfrozen.\n\n'
      + 'You can unfreeze later if changes are needed.'
    )) return;
    setFreezing(true);
    try {
      await apiClient.post(`/api/missions/${missionId}/freeze`, null);
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Freeze failed.');
    } finally {
      setFreezing(false);
    }
  }, [missionId, data, load]);

  const handleUnfreeze = useCallback(async () => {
    if (!missionId) return;
    if (!window.confirm(
      'Unfreeze this report?\n\nIt becomes editable again and will read the '
      + 'ACTIVE SOH — re-running Reconcile after a new SOH upload can change '
      + 'the delivered results.'
    )) return;
    setFreezing(true);
    try {
      await apiClient.post(`/api/missions/${missionId}/unfreeze`, null);
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Unfreeze failed.');
    } finally {
      setFreezing(false);
    }
  }, [missionId, load]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-10 border-b bg-white px-3 sm:px-6 py-3 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-base truncate">
            Inventory Report{missionTitle ? ` — ${missionTitle}` : ''}
          </h1>
          {summary && (
            <p className="text-xs text-muted-foreground">
              {summary.total.toLocaleString()} locations · {summary.match.toLocaleString()} match ·{' '}
              {discrepancyTotal.toLocaleString()} need attention
            </p>
          )}
        </div>
        {/* Customer co-brand logos — top-right of the report header. */}
        <div className="hidden sm:flex items-center gap-2 mr-1">
          <img src="/logos/dhl.png" alt="DHL" className="h-5 w-auto object-contain" />
          <img src="/logos/celcomdigi.png" alt="CelcomDigi" className="h-5 w-auto object-contain" />
        </div>
        {/* Report freeze — lock results + pin SOH for the next report cycle. */}
        {frozenAt ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 text-sky-800 border border-sky-300 px-2.5 py-1 text-xs font-semibold"
            title={`Report frozen since ${new Date(frozenAt).toLocaleString()} — results are read-only and pinned to the SOH at freeze time.`}
          >
            <Lock className="h-3.5 w-3.5" />
            Frozen — {new Date(frozenAt).toLocaleDateString()}
            {isManager && (
              <button
                type="button"
                onClick={handleUnfreeze}
                disabled={freezing}
                className="ml-1 inline-flex items-center gap-0.5 text-sky-700 hover:text-sky-900 underline decoration-dotted"
                title="Unfreeze to make changes"
              >
                <LockOpen className="h-3 w-3" /> unfreeze
              </button>
            )}
          </span>
        ) : (isManager && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleFreeze}
            disabled={freezing || !data}
            title="Lock all results and pin the current SOH — a new SOH upload cannot change this report"
          >
            <Lock className="h-4 w-4" /> {freezing ? 'Freezing…' : 'Freeze for Next Report'}
          </Button>
        ))}
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} title="Refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button size="sm" onClick={handleExport} disabled={exporting || !data}>
          <Download className="h-4 w-4" /> {exporting ? 'Exporting…' : 'Export Excel'}
        </Button>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* ── Frozen banner ─────────────────────────────────────────────────── */}
        {frozenAt && (
          <div className="flex items-start gap-2 rounded-lg border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            <Lock className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">This report is frozen — locked for the next report cycle.</p>
              <p className="text-sky-700 text-xs mt-0.5">
                Results, photos and comments are read-only and pinned to the SOH snapshot from{' '}
                {new Date(frozenAt).toLocaleString()}. A new SOH upload covering these locations will not
                change this report{isManager ? ' until you unfreeze it.' : '.'}
              </p>
            </div>
          </div>
        )}

        {/* ── Filters + search ──────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All{summary ? ` (${summary.total})` : ''}
          </Button>
          {RESULT_FILTERS.map(category => (
            <Button
              key={category}
              variant={filter === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(category)}
            >
              {category === 'MissingQR' ? 'Missing QR' : category}
            </Button>
          ))}

          <div className="relative w-full sm:w-auto sm:ml-auto">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search location…"
              className="h-9 w-full sm:w-56 rounded-md border border-input bg-white pl-8 pr-3 text-sm
                         focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchComment}
              onChange={e => setSearchComment(e.target.value)}
              placeholder="Search comments…"
              className="h-9 w-full sm:w-56 rounded-md border border-input bg-white pl-8 pr-3 text-sm
                         focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {/* ── States ────────────────────────────────────────────────────────── */}
        {loading && (
          <div className="text-sm text-muted-foreground py-12 text-center">
            Loading reconciliation records…
          </div>
        )}
        {error && !loading && (
          <div className="text-sm text-red-600 py-12 text-center">{error}</div>
        )}
        {!loading && !error && records.length === 0 && (
          <div className="text-sm text-muted-foreground py-12 text-center">
            {filter !== 'all' || search
              ? 'No locations match the current filter.'
              : 'No reconciliation records for this mission yet.'}
          </div>
        )}

        {/* ── Data table (virtualized — only visible rows mount) ─────────────── */}
        {!loading && !error && records.length > 0 && (
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="overflow-auto rounded-lg border bg-white"
            style={{ maxHeight: VIEWPORT_HEIGHT }}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">
                    <button
                      type="button"
                      onClick={toggleSort}
                      className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                    >
                      Location <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>
                    </button>
                  </TableHead>
                  {usesCevaPlaceholderLayout(profile) ? (
                    <>
                      <TableHead className="whitespace-nowrap">Layout Status</TableHead>
                      <TableHead className="whitespace-nowrap">Result</TableHead>
                      <TableHead className="whitespace-nowrap">Timestamp</TableHead>
                      <TableHead className="whitespace-nowrap">Image</TableHead>
                    </>
                  ) : usesNbcLayout(profile) ? (
                    <>
                      <TableHead className="whitespace-nowrap">Aisle</TableHead>
                      <TableHead className="whitespace-nowrap">{nbcIdentityHeaders(profile).detected}</TableHead>
                      <TableHead className="text-right whitespace-nowrap">OH Detected</TableHead>
                      <TableHead className="whitespace-nowrap">Timestamp</TableHead>
                      <TableHead className="whitespace-nowrap">{nbcIdentityHeaders(profile).wms}</TableHead>
                      <TableHead className="text-right whitespace-nowrap">OH SOH</TableHead>
                      <TableHead className="whitespace-nowrap">Result</TableHead>
                      <TableHead className="whitespace-nowrap">ITEM_ID</TableHead>
                      <TableHead className="whitespace-nowrap">ITEM_DESCRIPTION</TableHead>
                      <TableHead className="whitespace-nowrap">COMMENTS</TableHead>
                      <TableHead className="whitespace-nowrap">Image</TableHead>
                    </>
                  ) : usesOatsideLayout(profile) ? (
                    <>
                      <TableHead className="whitespace-nowrap">{identityHeaders(profile).detected}</TableHead>
                      <TableHead className="whitespace-nowrap">Description</TableHead>
                      <TableHead className="whitespace-nowrap">{identityHeaders(profile).wms}</TableHead>
                      <TableHead className="whitespace-nowrap">Description WMS</TableHead>
                      <TableHead className="whitespace-nowrap">Item WMS</TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead className="whitespace-nowrap">Detected QR Code</TableHead>
                      <TableHead className="whitespace-nowrap">Description</TableHead>
                      <TableHead className="whitespace-nowrap">Description WMS</TableHead>
                      <TableHead className="whitespace-nowrap">SKU Batch Number WMS</TableHead>
                      <TableHead className="whitespace-nowrap">Item Prefix</TableHead>
                      <TableHead className="whitespace-nowrap">Product Status</TableHead>
                    </>
                  )}
                  {!usesNbcLayout(profile) && !usesCevaPlaceholderLayout(profile) && (
                    <>
                      <TableHead className="text-right whitespace-nowrap">{countHeader(profile)}</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Drone Count</TableHead>
                      <TableHead className="whitespace-nowrap">Result</TableHead>
                      <TableHead className="whitespace-nowrap">Timestamp</TableHead>
                      <TableHead className="whitespace-nowrap">Comments</TableHead>
                      <TableHead className="whitespace-nowrap">Image</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {padTop > 0 && (
                  <tr aria-hidden="true"><td colSpan={colCount} style={{ height: padTop, padding: 0 }} /></tr>
                )}
                {visibleRecords.map(r => (
                  <ReportTableRow
                    key={r.id}
                    record={r}
                    profile={profile}
                    editable={editable}
                    onSaved={load}
                  />
                ))}
                {padBottom > 0 && (
                  <tr aria-hidden="true"><td colSpan={colCount} style={{ height: padBottom, padding: 0 }} /></tr>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
